import {
  chunkProductReviewSummaryIds,
  fetchProductReviewSummaryBatch,
} from 'src/lib/trusted-shops/product-review-summaries.client'
import type { ProductReviewSummary } from 'src/lib/trusted-shops/product-reviews'

type EcommerceItem = Record<string, unknown> & {
  item_id?: unknown
  item_category?: unknown
}

type EcommerceEvent = Record<string, unknown> & {
  event?: unknown
  ecommerce?: Record<string, unknown> & { items?: unknown }
}

const REVIEW_EVENTS = new Set(['add_to_cart', 'purchase'])
const EXPERIENCE_ITEM_ID = /^E-(\d+)$/

export async function enrichExperienceReviewItems(
  events: EcommerceEvent[]
): Promise<EcommerceEvent[]> {
  const publicIds = getExperiencePublicIds(events)
  if (!publicIds.length) return events

  const summaries = await loadReviewSummaries(publicIds)

  return events.map(event => {
    if (typeof event.event !== 'string' || !REVIEW_EVENTS.has(event.event)) return event
    if (!Array.isArray(event.ecommerce?.items)) return event

    return {
      ...event,
      ecommerce: {
        ...event.ecommerce,
        items: event.ecommerce.items.map(item =>
          isEcommerceItem(item) ? enrichItem(item, summaries) : item
        ),
      },
    }
  })
}

function getExperiencePublicIds(events: EcommerceEvent[]) {
  const ids = new Set<number>()

  for (const event of events) {
    if (typeof event.event !== 'string' || !REVIEW_EVENTS.has(event.event)) continue
    if (!Array.isArray(event.ecommerce?.items)) continue

    for (const item of event.ecommerce.items) {
      if (!isExperienceItem(item)) continue
      const publicId = getExperiencePublicId(item)
      if (publicId) ids.add(publicId)
    }
  }

  return [...ids]
}

async function loadReviewSummaries(publicIds: number[]) {
  const summaries: Record<string, ProductReviewSummary> = {}

  for (const batch of chunkProductReviewSummaryIds(publicIds)) {
    try {
      Object.assign(summaries, await fetchProductReviewSummaryBatch(batch))
    } catch {
      // Tracking must not block cart or checkout when Trusted Shops is unavailable.
    }
  }

  return summaries
}

function enrichItem(
  item: EcommerceItem,
  summaries: Record<string, ProductReviewSummary>
): EcommerceItem {
  const enriched = { ...item }
  delete enriched.item_has_reviews
  delete enriched.item_review_count

  if (!isExperienceItem(item)) return enriched

  const publicId = getExperiencePublicId(item)
  const count = publicId ? (summaries[String(publicId)]?.count ?? 0) : 0

  return {
    ...enriched,
    item_has_reviews: count > 0 ? 'yes' : 'no',
    item_review_count: count,
  }
}

function isEcommerceItem(value: unknown): value is EcommerceItem {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function isExperienceItem(value: unknown): value is EcommerceItem {
  return isEcommerceItem(value) && value.item_category === 'experience'
}

function getExperiencePublicId(item: EcommerceItem) {
  if (typeof item.item_id !== 'string') return null
  const match = EXPERIENCE_ITEM_ID.exec(item.item_id)
  if (!match) return null
  const publicId = Number(match[1])
  return Number.isSafeInteger(publicId) && publicId > 0 ? publicId : null
}
